import React, { useEffect, useMemo, useState } from 'react';
import {
  find,
  get,
  uniqBy,
  filter,
  flatMap,
  includes,
  findIndex,
  head,
  reduce,
} from 'lodash';
import { translate } from '../../../../utils/translate/translations';
import Compare from '../../../../common/compare/Compare';
import BodyCompareFilter from './BodyCompareFilter';
import {
  getAvailablePowertrains,
  getAvailableDriveAndWheelTypes,
  normalizeBodyCompareData,
  getCapabilitiesbyEngineRowId,
} from './helper';
import { driveTypeSectionEl } from '../../../../components/compareManager/filters/OptionsFilter';
import { getAppPath } from '../../../../components/compareManager/utils';

const BodyCompare = ({
  features,
  config,
  trimMatrix,
  isScrolled,
  appResources,
  selectedBodyTypeId,
  analyticsKey,
  onSelect,
  isUpdating,
  isFeatures = false,
  showFilters = true,
  capabilities,
}) => {
  const { bodyTypes, trims } = trimMatrix;
  const { styleIds: valuesByStyle } = features;
  const { cgiSize } = appResources;

  const selectedBodyType =
    find(bodyTypes, { id: selectedBodyTypeId }) || head(bodyTypes);

  const availablePowertrains = getAvailablePowertrains(trimMatrix, translate);
  const availableDriveAndWheels = getAvailableDriveAndWheelTypes(bodyTypes);

  const normalizedTrims = normalizeBodyCompareData(trims, bodyTypes, cgiSize);

  const trimsOrderByBodyType = reduce(
    bodyTypes,
    (result, bodyType) => {
      const matches = filter(
        normalizedTrims,
        (trim) => trim.bodyTypeId === bodyType.id
      );
      return (result = [...result, ...matches]);
    },
    []
  );

  const getFilteredData = (driveAndWheelType, powertrain) => {
    const driveTypes = flatMap(bodyTypes, 'driveTypes');

    const filteredDriveAndWheelTypes = filter(driveTypes, {
      driveType: driveAndWheelType.driveType,
      rearWheelType: driveAndWheelType.rearWheelType || null,
    });

    const styleIds = flatMap(filteredDriveAndWheelTypes, 'styleIds');

    const filteredTrims = filter(trimsOrderByBodyType, (trim) => {
      return (
        includes(styleIds, trim.styleId) &&
        find(trim.configurations, (configuration) => {
          return (
            configuration.engine.code === powertrain.engineCode &&
            configuration.transmission.code === powertrain.transmissionCode
          );
        })
      );
    });

    return uniqBy(filteredTrims, 'bodyTypeId');
  };

  const defaultTrim = find(normalizedTrims, {
    styleId: get(selectedBodyType, 'defaultTrim.styleId'),
  });

  const defaultConfiguration = head(get(defaultTrim, 'configurations'));
  const defaultDriveAndWheelType = find(availablePowertrains, {
    engineCode: defaultConfiguration.engineCode,
    transmissionCode: defaultConfiguration.transmissionCode,
  });

  const defaultPowertrain = find(availableDriveAndWheels, {
    driveType: defaultTrim.driveTypeId,
    rearWheelType: defaultTrim.rearWheelType || '',
  });

  const initFilteredData = getFilteredData(
    defaultDriveAndWheelType,
    defaultPowertrain
  );

  const [selectedPowertrain, setSelectedPowertrain] = useState(
    defaultDriveAndWheelType
  );
  const [selectedDriveAndWheelType, setSelectedDriveAndWheelType] = useState(
    defaultPowertrain
  );

  const [filteredData, setFilteredData] = useState(initFilteredData);

  const [headers, setHeaders] = useState();
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    setHeaders(
      filteredData?.map((data) => {
        return {
          imageUrl: data.cgiImageUrl,
          primaryText: data.description,
          msrp: data.msrp,
          trimKey: data.description,
          bodyTypeId: data.bodyTypeId,
          description: data.description,
        };
      })
    );
  }, [filteredData]);

  const updateSelectedIndex = (data) => {
    const index = findIndex(
      data,
      (bodyType) => bodyType.description === selectedBodyType.description
    );

    if (index === -1) {
      setSelectedIndex(selectedIndex >= data.length ? 0 : selectedIndex);
    } else {
      setSelectedIndex(index);
    }
  };

  useEffect(() => {
    const data = getFilteredData(selectedDriveAndWheelType, selectedPowertrain);
    updateSelectedIndex(data);

    setFilteredData(data);
  }, [selectedDriveAndWheelType, selectedPowertrain]);

  const rowHeaders = {
    Dimensions: features.groups.Dimensions,
    ...capabilities?.groups,
  };

  const buildTrimPath = (styleId) => {
    const currentPath = location.pathname;
    const pathName = getAppPath(currentPath);

    return `${pathName}/trims?styleId=${styleId}&hideReveal=true`;
  };

  const columns = useMemo(() => {
    return (
      filteredData?.map((data) => {
        return {
          data: data,
          selectionLink: buildTrimPath(data.styleId),
          groups: {
            ...valuesByStyle[data.styleId],
            ...getCapabilitiesbyEngineRowId(
              capabilities,
              data.bodyTypeId,
              selectedDriveAndWheelType,
              selectedPowertrain
            ),
          },
        };
      }) || []
    );
  }, [filteredData]);

  const onSelectConfiguration = (column) => {
    const { data } = column;
    const configuration = find(data.configurations, {
      engineCode: selectedPowertrain.engineCode,
      transmissionCode: selectedPowertrain.transmissionCode,
    });
    onSelect(configuration);
  };

  const onFilterChange = (driveAndWheelType, powertrain) => {
    setSelectedDriveAndWheelType(driveAndWheelType);
    setSelectedPowertrain(powertrain);
  };

  const onDriveChange = (e) => {
    const drive = find(availableDriveAndWheels, { id: e.target.value });
    setSelectedDriveAndWheelType(drive);
  };

  const driveTypeAnalyticsKey = `${analyticsKey}:filters:drive type`;
  const driveTypeSection = driveTypeSectionEl(
    true,
    selectedDriveAndWheelType,
    availableDriveAndWheels,
    onDriveChange,
    translate,
    driveTypeAnalyticsKey
  );

  const showFilterExpander = showFilters && availablePowertrains.length > 1;
  const showHeaderFilter =
    showFilters && !showFilterExpander && availableDriveAndWheels.length > 1;
  const filterCount = filteredData?.length;
  const filterCountLabel =
    filterCount === 1
      ? 'feature.filter.body.count.v3'
      : 'feature.filter.body.count.plural.v3';
  const filterCountText = translate(filterCountLabel, {
    numberOfBodySyles: filterCount,
  });

  return (
    <>
      {showFilterExpander && (
        <BodyCompareFilter
          getFilteredData={getFilteredData}
          filterCountText={filterCountText}
          availablePowertrains={availablePowertrains}
          selectedPowertrain={selectedPowertrain}
          availableDriveAndWheels={availableDriveAndWheels}
          selectedDriveAndWheel={selectedDriveAndWheelType}
          onFilterChange={onFilterChange}
          selectedBodyTypeId={selectedBodyType.id}
          isUpdating={isUpdating}
          analyticsKey={analyticsKey}
        />
      )}
      <Compare
        headers={headers}
        columns={columns}
        compareGroups={rowHeaders}
        config={config}
        onSelect={onSelectConfiguration}
        isScrolled={isScrolled}
        analyticsKey={analyticsKey}
        selectedIndex={selectedIndex}
        displaySort={false}
        displayFilterCount={true}
        filterText={filterCountText}
        displayInTableFilter={showHeaderFilter}
        driveTypeSectionEl={driveTypeSection}
        isFeatures={isFeatures}
      />
    </>
  );
};

export default BodyCompare;
